import { useEffect, useState } from 'react'

/**
 * React hook that reflects the user’s OS-level “Reduce motion” accessibility setting.
 *
 * - SSR-safe: returns `false` when `window` / `matchMedia` is unavailable.
 * - Subscribes to changes so it stays up to date while the app is open.
 *
 * @returns {boolean} `true` if reduced motion is preferred; otherwise `false`.
 */
export default function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(prefers-reduced-motion: reduce)')?.matches ?? false
  })

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return undefined

    // Keep a stable MediaQueryList reference for subscription.
    const mediaQueryList = window.matchMedia('(prefers-reduced-motion: reduce)')

    const handleChange = (event) => {
      setPrefersReducedMotion(event.matches)
    }

    // Modern browsers use addEventListener/removeEventListener
    // Safari < 14 used addListener/removeListener but is no longer supported
    mediaQueryList.addEventListener('change', handleChange)
    return () => mediaQueryList.removeEventListener('change', handleChange)
  }, [])

  return prefersReducedMotion
}
